import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { MenuSectionProps } from '@procsea/design-system'

import { APPLI_URL } from 'src/constants/constants'
import {
  useConnectedBuyerMembership,
  useCurrentMembership,
  useSeller,
  useUserType,
} from 'src/contexts/userContext'
import { useLocale } from 'src/hooks/useLocale'
import { useOptionFlags } from 'src/hooks/useOptionFlags'
import { useUserCounts } from 'src/hooks/useUserCounts'
import { usePaginatedDashboardsQuery as useBuyerPaginatedDashboardsQuery } from 'src/queries/buyer/dashboards'
import { usePaginatedDashboardsQuery as useSellerPaginatedDashboardsQuery } from 'src/queries/seller/dashboards'
import { UserType } from 'src/types/router'

import useDefaultMarketplaceId from '../buyer-dashboard/shared/hooks/marketplaces/useDefaultMarketplaceId'
import { useUnreadBuyerChatMessagesCount } from '../chat/chat-buyer/useBuyerChatStore'
import { useUnreadSellerChatMessagesCount } from '../chat/chat-seller/useSellerChatStore'
import { MenuEntryLinkProps } from './MainMenu/MenuEntryLink/MenuEntryLink'
import { getIsMenuReady } from './MainMenu/mainMenu.utils'

type MenuItemConfig = MenuEntryLinkProps

interface MenuSectionConfigBase extends Omit<MenuSectionProps, 'isVisible'> {
  entries: MenuItemConfig[]
}

interface MenuSectionConfig extends MenuSectionProps {
  entries: MenuItemConfig[]
}

type MenuConfigBase = MenuSectionConfigBase[]

type MenuConfig = {
  activeMenuEntry?: string
  isMenuReady: boolean
  menuSections: MenuSectionConfig[]
}

export const useMenuConfig = (): MenuConfig => {
  const [buyerMarketplaceId, setBuyerMarketplaceId] = useState<Id | undefined>()
  const seller = useSeller()
  const currentMembership = useCurrentMembership()
  const locale = useLocale()
  const { pathname } = useLocation()
  const { isBuyer, isSeller } = useUserType()
  const defaultBuyerMarketplaceId = useDefaultMarketplaceId()
  const unreadBuyerChatMessagesCount = useUnreadBuyerChatMessagesCount()
  const unreadSellerChatMessagesCount = useUnreadSellerChatMessagesCount()
  const connectedBuyerMembership = useConnectedBuyerMembership()

  useEffect(() => {
    if (defaultBuyerMarketplaceId) {
      setBuyerMarketplaceId(defaultBuyerMarketplaceId)
    }
  }, [defaultBuyerMarketplaceId])

  const {
    optionFlagCanUseCatalog,
    optionFlagCanUseChatBuyer,
    optionFlagCanUseChatSeller,
    optionFlagCanUseCustomerCategories,
    optionFlagCanUseCustomers,
    optionFlagCanUseManagement,
    optionFlagCanUseMapping,
    optionFlagCanUseMarket,
    optionFlagCanUsePriceSheets,
    optionFlagCanUsePurchaseInvoices,
    optionFlagCanUsePurchaseOrders,
    optionFlagCanUseCrossDockingPurchaseOrders,
    optionFlagCanUseSalesInvoices,
    optionFlagCanUseSalesOrders,
    optionFlagCanUseStock,
    optionFlagCanUseSuppliers,
  } = useOptionFlags()

  const {
    totalPendingClaimsCount = 0,
    totalTodayOrdersToPrepareCount = 0,
    unmappedPurchasedProductsCount,
    unsyncOrdersByVerticalCount,
    waitingInvoicesCount,
  } = useUserCounts()

  const { data: sellerDashboardPages } = useSellerPaginatedDashboardsQuery({
    queryParams: { limit: 1 },
    queryOptions: { enabled: isSeller },
  })

  const { data: buyerDashboardPages } = useBuyerPaginatedDashboardsQuery({
    queryParams: { limit: 1 },
    queryOptions: { enabled: isBuyer || !!connectedBuyerMembership },
    contextualMembershipId: isBuyer ? undefined : connectedBuyerMembership?.id,
  })

  const sellerAnalyticsFirstTab = sellerDashboardPages?.pages?.[0]?.results?.[0]?.tabSlug

  const buyerAnalyticsFirstTab = buyerDashboardPages?.pages?.[0]?.results?.[0]?.tabSlug

  const isMenuReady = getIsMenuReady({
    hasBuyerMarketplaceId: !!buyerMarketplaceId,
    hasConnectedBuyerMembership: !!connectedBuyerMembership,
    hasSeller: !!seller,
    isBuyer,
    isSeller,
  })

  const menuConfigBase: MenuConfigBase = [
    {
      'data-e2e': 'menu-procurement-section',
      title: gettext('Procurement'),
      entries: [
        {
          'data-e2e': 'menu-entry-market',
          icon: 'cart-shopping',
          isVisible: optionFlagCanUseMarket,
          label: gettext('Purchase'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/market/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/market/`,
        },
        {
          'data-e2e': 'menu-entry-purchase-orders',
          icon: 'truck-fast',
          isVisible: optionFlagCanUsePurchaseOrders,
          label: gettext('Purchase orders (new menu)'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/orders/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/orders/`,
        },
        {
          'data-e2e': 'menu-entry-cross-docking-purchase-orders',
          count: unsyncOrdersByVerticalCount,
          icon: 'truck-fast',
          isVisible: optionFlagCanUseCrossDockingPurchaseOrders,
          label: gettext('Purchase orders (new menu)'),
          to: `/${locale}${APPLI_URL.SELLER.PURCHASE_ORDERS}`,
        },
        {
          'data-e2e': 'menu-entry-purchase-invoicing',
          icon: 'receipt',
          isVisible: optionFlagCanUsePurchaseInvoices,
          label: gettext('Purchase invoices'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/invoicing/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/invoicing/`,
        },
        {
          'data-e2e': 'menu-entry-purchase-analytics',
          icon: 'chart-column',
          isVisible:
            !!buyerAnalyticsFirstTab &&
            (currentMembership.isBuyer ||
              currentMembership.releaseToggleAccessBuyerFeaturesAsASeller),
          label: gettext('Purchase analytics'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/analytics/${buyerAnalyticsFirstTab}/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/analytics/${buyerAnalyticsFirstTab}/`,
        },
        {
          'data-e2e': 'menu-entry-suppliers',
          icon: 'users',
          isVisible: optionFlagCanUseSuppliers,
          label: gettext('Suppliers'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/suppliers/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/suppliers/`,
        },
        {
          'data-e2e': 'menu-entry-price-sheets',
          icon: 'chart-mixed-up-circle-dollar',
          isVisible: optionFlagCanUsePriceSheets,
          label: gettext('Price sheets'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/price-sheets/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/price-sheets/`,
        },
        {
          'data-e2e': 'menu-entry-buyer-chat',
          count: unreadBuyerChatMessagesCount,
          icon: 'comments',
          isVisible: optionFlagCanUseChatBuyer,
          label: gettext('Supplier chat'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/chat/`
            : `/${locale}${APPLI_URL.SELLER.MARKETPLACE}${buyerMarketplaceId}/chat/`,
        },
      ],
    },
    {
      'data-e2e': 'menu-catalog-management-section',
      title: gettext('Catalog Management'),
      entries: [
        {
          'data-e2e': 'menu-entry-sell',
          icon: 'book',
          isActive:
            pathname.includes(`/${locale}${APPLI_URL.SELLER.MARKET.BASE}`) ||
            pathname.includes(`/${locale}${APPLI_URL.SELLER.PRODUCT.BASE}`),
          isVisible: optionFlagCanUseCatalog,
          label: gettext('Product catalog'),
          to: `/${locale}${APPLI_URL.SELLER.MARKET.LIVE}`,
        },
        {
          'data-e2e': 'menu-entry-management',
          icon: 'list-check',
          isVisible: optionFlagCanUseManagement,
          label: gettext('Assortment'),
          to: `/${locale}${APPLI_URL.SELLER.MANAGEMENT.BASE}`,
        },

        {
          'data-e2e': 'menu-entry-mapping',
          count: unmappedPurchasedProductsCount,
          icon: 'link',
          isVisible: optionFlagCanUseMapping,
          label: gettext('Product mapping'),
          to: isBuyer
            ? `/${locale}/${UserType.BUYER}/marketplace/${buyerMarketplaceId}/mapping/purchases/`
            : `/${locale}/${APPLI_URL.SELLER.MAPPING.PURCHASES}`,
        },
        {
          'data-e2e': 'menu-entry-stock',
          icon: 'boxes-stacked',
          isVisible: optionFlagCanUseStock,
          label: gettext('Inventory'),
          to: `/${locale}${APPLI_URL.SELLER.STOCK_MANAGEMENT}`,
        },
      ],
    },
    {
      'data-e2e': 'menu-sales-section',
      title: gettext('Sales'),
      entries: [
        {
          'data-e2e': 'menu-entry-sales-orders',
          count: totalTodayOrdersToPrepareCount + totalPendingClaimsCount,
          icon: 'truck-fast',
          isVisible: optionFlagCanUseSalesOrders,
          label: gettext('Sales orders'),
          to: `/${locale}${APPLI_URL.SELLER.ORDERS.BASE}`,
        },
        {
          'data-e2e': 'menu-entry-sales-invoicing',
          count: waitingInvoicesCount,
          icon: 'receipt',
          isVisible: optionFlagCanUseSalesInvoices,
          label: gettext('Sales invoices'),
          to: `/${locale}${APPLI_URL.SELLER.INVOICING.BASE}`,
        },
        {
          'data-e2e': 'menu-entry-sales-analytics',
          icon: 'chart-column',
          isVisible: !!sellerAnalyticsFirstTab,
          label: gettext('Sales analytics'),
          to: `/${locale}${APPLI_URL.SELLER.ANALYTICS}${sellerAnalyticsFirstTab}/`,
        },
        {
          'data-e2e': 'menu-entry-customer-categories',
          icon: 'user-tag',
          isVisible: optionFlagCanUseCustomerCategories,
          label: gettext('Customer categories (new menu)'),
          to: `/${locale}/${UserType.SELLER}/customer-categories/`,
        },
        {
          'data-e2e': 'menu-entry-customers',
          icon: 'users',
          isVisible: optionFlagCanUseCustomers,
          label: gettext('Customers'),
          to: `/${locale}${APPLI_URL.SELLER.CUSTOMERS.BASE}`,
        },
        {
          'data-e2e': 'menu-entry-seller-chat',
          count: unreadSellerChatMessagesCount,
          icon: 'comments',
          isVisible: optionFlagCanUseChatSeller,
          label: gettext('Customer chat'),
          to: `/${locale}${APPLI_URL.SELLER.CHAT}`,
        },
      ],
    },
  ]

  const menuSections: MenuSectionConfig[] = menuConfigBase.map(menuSection => ({
    ...menuSection,
    isVisible: menuSection.entries.some(menuEntry => menuEntry.isVisible),
    entries: menuSection.entries.map(menuEntry => ({
      ...menuEntry,
      isActive: !!menuEntry.isActive || pathname.includes(menuEntry.to),
    })),
  }))

  const ActiveEntryLabel = menuSections
    .flatMap(menuSection => menuSection.entries)
    .find(menuEntry => menuEntry.isActive)?.label

  const menuConfig: MenuConfig = {
    activeMenuEntry: typeof ActiveEntryLabel === 'string' ? ActiveEntryLabel : undefined,
    isMenuReady,
    menuSections,
  }

  return menuConfig
}
